import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { AdminTable } from "../components/AdminTable";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as usersApi from "../api/users";

export default function AdminUserTable() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [searchEmail, setSearchEmail] = useState("");
  const debouncedSearchEmail = useDebouncedValue(searchEmail, 400);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_users");
  const { hasPermission: canEdit } = useViewPermission("edit_users");
  const { run } = useMutationLock();

  const fetchUsers = async (pageArg = page, searchArg = debouncedSearchEmail) => {
    try {
      if (!hasLoadedOnce) setLoading(true);
      const response = await usersApi.list({
        page: pageArg,
        limit,
        search: searchArg,
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setUsers(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (!hasPermission) return;
    setPage(1);
    fetchUsers(1, debouncedSearchEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, debouncedSearchEmail]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchUsers(newPage, debouncedSearchEmail);
  };

  const handleRefresh = () => {
    setPage(1);
    fetchUsers(1, debouncedSearchEmail);
  };

  const columnKeys = [
    "email",
    // "id",
    "name",
    "phoneNumber",
    "parentsPhoneNumber",
    "address",
    "city",
    "province",
    "country",
    "school",
    "grade",
  ];

  const handleEdit = (index, field, value) => {
    if (!canEdit) return;
    setUsers((prev) =>
      prev.map((u, i) => (i === index ? { ...u, [field]: value } : u)),
    );
  };

  const handleSave = async (id, field, value) => {
    if (!canEdit) return;
    await run(async () => {
      try {
        await usersApi.update(id, { field, value });
        notificationCtx.show({
          message: "User field updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating user field:", error);
        notificationCtx.show({
          message: "Failed to update user field.",
          stat: "error",
        });
      }
    });
  };

  const handleUpdateListStatus = async (id, payload) => {
    if (!canEdit) {
      throw new Error("Missing edit permission");
    }
    return run(async () => {
      await usersApi.updateListStatus(id, payload);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id
            ? {
                ...u,
                blackListed: Boolean(payload.blackListed),
                greyListed: Boolean(payload.greyListed),
                listReason: payload.blackListed || payload.greyListed
                  ? payload.reason || null
                  : null,
              }
            : u,
        ),
      );
      if (payload.blackListed || payload.greyListed) {
        notificationCtx.show({
          message: payload.blackListed
            ? "Student black listed."
            : "Student grey listed.",
          stat: "success",
        });
      }
    });
  };

  const handleDelete = (userId) => {
    if (!canEdit) return;

    const confirmDeleteUser = async () => {
      try {
        await usersApi.remove(userId);

        const nextPage = users.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchUsers(nextPage, debouncedSearchEmail);
        notificationCtx.show({
          message: "User deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete user",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message:
        "Are you sure you want to delete this user? This will also delete all registrations associated with this user.",
      stat: "info",
      onConfirm: confirmDeleteUser,
    });
  };

  if (authLoading || permLoading || (loading && !hasLoadedOnce)) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="mb-3">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              margin: "10px 50px 0",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <AdminTable
              data={users}
              columnKeys={columnKeys}
              handleEdit={handleEdit}
              handleSave={handleSave}
              handleDelete={handleDelete}
              canEdit={canEdit}
              onRefresh={handleRefresh}
              onUpdateListStatus={handleUpdateListStatus}
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              searchEmail={searchEmail}
              onSearchEmailChange={setSearchEmail}
            />
          </div>
        </div>
      )}
    </div>
  );
}
